import type { Match, Query, RouterContext } from '@atlassian/react-resource-router';
import type { RapidViewRouteConfig } from './RapidViewRouteConfig.types.tsx';

import {
	WRM_BOARD_RESOURCES_FHD_ARRAY,
	WRM_BACKLOG_RESOURCES_FHD_ARRAY,
	WRM_RESOURCES_ARRAY,
	WRM_HERITAGE_RESOURCES_FHD_ARRAY,
} from './resourceTypeRapidboardsWrmBundle.constants.tsx';

const RAPID_VIEW_ROUTE_CONFIG: Record<string, RapidViewRouteConfig> = {
	BOARD: {
		routes: [
			'rapidboard-board',
			'rapidboard-board-embed',
			'rapidboard-user-board',
			'servicedesk-boards',
			'servicedesk-queues-custom-board',
			'servicedesk-queues-practice-custom-board',
		],
		resources: WRM_BOARD_RESOURCES_FHD_ARRAY,
		stateKey: 'board-state',
	},
	BACKLOG: {
		routes: ['rapidboard-backlog', 'rapidboard-user-backlog', 'rapidboard-backlog-embed'],
		resources: WRM_BACKLOG_RESOURCES_FHD_ARRAY,
		stateKey: 'backlog-state',
	},
	DEFAULT: {
		resources: WRM_RESOURCES_ARRAY,
		stateKey: 'report-state',
	},
} as const;

export const buildAllDataQueryParams = (query: Query, match: Match): string => {
	const { rapidView, quickFilter, projectKey: queryProjectKey, etag } = query;
	const boardId = match.params.boardId || rapidView;
	const projectKey = match.params.projectKey || queryProjectKey;

	let activeQuickFilters = '';
	if (quickFilter) {
		activeQuickFilters = Array.isArray(quickFilter)
			? quickFilter.reduce(
					(prevFilter, nextFilter) => `${prevFilter}&activeQuickFilters=${nextFilter}`,
					activeQuickFilters,
				)
			: `&activeQuickFilters=${quickFilter}`;
	}

	const etagParam = etag ? `&etag=${etag}` : '';

	const projectKeyParam = projectKey ? `&selectedProjectKey=${projectKey}` : '';

	const { GH } = window;

	const forceConsistency = '&forceConsistency=true';
	GH?.RapidBoard?.State?.setRapidViewIdForEventuallyConsistentBoard(undefined);

	return `rapidViewId=${boardId}${projectKeyParam}${activeQuickFilters}${etagParam}${forceConsistency}`;
};

const cleanUpInlineScript = (dataName: string) => {
	// @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	delete window[dataName];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getInlineScriptData = (dataName: string): Promise<any> | null => {
	// @ts-expect-error - TS7015 - Element implicitly has an 'any' type because index expression is not of type 'number'.

	// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
	const req: XMLHttpRequest = window[dataName];
	if (!req) {
		return null;
	}

	if (req.readyState === req.DONE && req.status !== 200) {
		cleanUpInlineScript(dataName);
		return null;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	return new Promise<any>((r: (result: Promise<any> | any) => void) => {
		if (req.readyState === req.DONE && req.status === 200) {
			cleanUpInlineScript(dataName);
			r(JSON.parse(req.response));
		} else {
			req.addEventListener('load', () => {
				cleanUpInlineScript(dataName);
				r(JSON.parse(req.response));
			});
		}
	});
};

export const getRapidViewConfigFromRouteName = (routeName: string): RapidViewRouteConfig => {
	for (const routeConfig of Object.values(RAPID_VIEW_ROUTE_CONFIG)) {
		if (routeConfig.routes?.includes(routeName)) {
			return routeConfig;
		}
	}
	return RAPID_VIEW_ROUTE_CONFIG.DEFAULT;
};

export const getRequiredResourceList = (view: string | null): string[] => {
	if (view) {
		const routeConfig = getRapidViewConfigFromRouteName(view);
		return [...WRM_HERITAGE_RESOURCES_FHD_ARRAY, ...routeConfig.resources];
	}
	return [...WRM_HERITAGE_RESOURCES_FHD_ARRAY, ...RAPID_VIEW_ROUTE_CONFIG.DEFAULT.resources];
};

export function getConfigFromRouterContext(routerContext: Partial<RouterContext>): string {
	return routerContext.match?.params.config || routerContext.query?.config || '';
}
